import { collabDatabaseBatch } from '@databases/collab-state-manager/batch';
import {
  type Database,
  type DatabaseEntry,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { convertType } from '@databases/utils/database/field-types';
import {
  getContentLabels,
  getReferencedLabelContentRefs
} from '@databases/utils/database/field-types/pageLabels';
import { getContentStatus } from '@databases/utils/database/field-types/pageStatus';
import { createEntry, getDatabaseValue } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { batch } from '@databases/utils/redux/redux';
import { type AnyAction } from 'redux';
import { type ThunkAction } from 'redux-thunk';

import { addChangeToDraft } from '../changesets/actions';
import { applyDeltaAfterPaste } from '../paste/actions';
import { type DefaultRootState } from '../redux';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

const getSourceValueText = (
  database: Database,
  sourceField: DatabaseField,
  sourceEntry: DatabaseEntry,
  sourceValue: DatabaseValue,
  getState: () => DefaultRootState
): string => {
  const sourceContentRefs = getReferencedLabelContentRefs(
    { database, field: sourceField, entry: sourceEntry },
    getState()
  );
  const convertedSourceValue = convertType(
    {
      database,
      field: sourceField,
      value: sourceValue
    },
    DatabaseFieldType.TEXT,
    getState()
  );

  switch (sourceField.type) {
    case DatabaseFieldType.PAGE_LABELS: {
      return sourceContentRefs
        .map((contentRef) => getContentLabels(contentRef, {}, getState())?.join('\n'))
        .join('\n');
    }
    case DatabaseFieldType.PAGE_STATUS: {
      return sourceContentRefs
        .map((contentRef) => getContentStatus(contentRef, {}, getState())?.name ?? '')
        .join('\n');
    }
    default:
      return convertedSourceValue.value.plain;
  }
};

export const doFillValues =
  (
    contentId: string,
    selectedFieldIds: string[],
    selectedEntryIds: string[],
    sourceEntryIds: string[],
    sourceFieldIds: string[],
    newValue?: DatabaseValue
  ): Dispatcher<void> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId
    });
    const targetEntryIds = selectedEntryIds.filter(
      (entryId) => !sourceEntryIds.includes(entryId)
    );
    batch(() => {
      collabDatabaseBatch({ dispatch, getState }, contentId, () => {
        let recurringEntryIdx = 0;
        targetEntryIds.forEach((entryId) => {
          selectedFieldIds.forEach((fieldId, fieldIdx) => {
            const sourceEntry = database.entriesById[sourceEntryIds[recurringEntryIdx]];
            const sourceField = database.fieldsById[sourceFieldIds[fieldIdx]];
            const sourceValue =
              newValue || getDatabaseValue(database, sourceField, sourceEntry);

            const targetEntry = database.entriesById[entryId];
            const targetField = database.fieldsById[fieldId];

            const text = getSourceValueText(
              database,
              sourceField,
              sourceEntry,
              sourceValue,
              getState
            );

            const changes = applyDeltaAfterPaste(
              {
                value: sourceValue,
                field: targetField,
                entry: targetEntry,
                database,
                text
              },
              { dispatch, getState }
            );
            changes?.forEach((change) => {
              dispatch(
                addChangeToDraft(contentId, {
                  type: DatabaseChangeType.SET_VALUE,
                  fieldId: fieldId,
                  entryId: entryId,
                  value: sourceValue,
                  change: change
                })
              );
            });
          });
          recurringEntryIdx++;
          if (recurringEntryIdx >= sourceEntryIds.length - 1) {
            recurringEntryIdx = 0;
          }
        });
      });
    });
  };

export const doAddEntry =
  (
    contentId: string,
    viewSettings: DatabaseViewSettings | undefined
  ): Dispatcher<DatabaseEntry> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdRequired(getState(), {
      contentId
    });
    const entry = createEntry(database, viewSettings);
    dispatch(addChangeToDraft(contentId, { type: DatabaseChangeType.ADD_ENTRY, entry }));
    return entry;
  };
